/**
 * Telnyx Provisioning Store
 *
 * Drives the "Phone Numbers" tab on the AccountDetail page. Tracks per-tenant
 * Telnyx state (messaging profile + 10DLC brand/campaign + numbers) and
 * exposes the actions the UI needs:
 *   - load aggregate status
 *   - register brand / poll vetting
 *   - create campaign / poll approval
 *   - search and provision numbers
 *   - release numbers, set default, edit friendly name
 */
import { create } from 'zustand';
import { getApiClient, API_ENDPOINTS } from '@respectly/api-client';
import type {
  TelnyxStatusResponse,
  TelnyxAvailableNumber,
  TelnyxNumberSearchCriteria,
  TelnyxProvisionNumberRequest,
  TelnyxSubmitTfvRequest,
} from '@respectly/shared-types';

interface TelnyxStoreError {
  code: string;
  message: string;
}

interface TelnyxState {
  // Per-account state keyed by account id
  statusByAccount: Record<string, TelnyxStatusResponse>;
  loadingStatusByAccount: Record<string, boolean>;
  errorByAccount: Record<string, TelnyxStoreError | null>;

  // Search state (single-account at a time — only one search dialog open)
  searchResults: TelnyxAvailableNumber[];
  isSearching: boolean;
  searchError: TelnyxStoreError | null;

  // Action flags
  isCreatingProfile: boolean;
  isCreatingBrand: boolean;
  isRefreshingBrand: boolean;
  isCreatingCampaign: boolean;
  isRefreshingCampaign: boolean;
  isSubmittingTfv: boolean;
  isRefreshingTfv: boolean;
  isProvisioningNumber: boolean;
  numberActionError: TelnyxStoreError | null;

  // Actions
  loadStatus: (accountId: string) => Promise<void>;
  ensureProfile: (accountId: string) => Promise<boolean>;
  registerBrand: (accountId: string) => Promise<boolean>;
  refreshBrand: (accountId: string) => Promise<boolean>;
  createCampaign: (accountId: string) => Promise<boolean>;
  refreshCampaign: (accountId: string) => Promise<boolean>;
  submitTfv: (accountId: string, params: TelnyxSubmitTfvRequest) => Promise<boolean>;
  refreshTfv: (accountId: string) => Promise<boolean>;
  searchNumbers: (accountId: string, criteria: TelnyxNumberSearchCriteria) => Promise<void>;
  clearSearch: () => void;
  provisionNumber: (accountId: string, params: TelnyxProvisionNumberRequest) => Promise<boolean>;
  updateNumber: (
    accountId: string,
    phoneNumberId: string,
    params: { friendlyName?: string; isDefault?: boolean },
  ) => Promise<boolean>;
  releaseNumber: (accountId: string, phoneNumberId: string) => Promise<boolean>;
  clearError: (accountId?: string) => void;
}

function toError(err: unknown): TelnyxStoreError {
  if (err && typeof err === 'object') {
    const e = err as { code?: string; message?: string; error?: { code?: string; message?: string } };
    if (e.error) {
      return {
        code: e.error.code ?? 'UNKNOWN',
        message: e.error.message ?? 'Unknown error',
      };
    }
    return { code: e.code ?? 'UNKNOWN', message: e.message ?? 'Unknown error' };
  }
  return { code: 'UNKNOWN', message: typeof err === 'string' ? err : 'Unknown error' };
}

export const useTelnyxStore = create<TelnyxState>((set, get) => ({
  statusByAccount: {},
  loadingStatusByAccount: {},
  errorByAccount: {},

  searchResults: [],
  isSearching: false,
  searchError: null,

  isCreatingProfile: false,
  isCreatingBrand: false,
  isRefreshingBrand: false,
  isCreatingCampaign: false,
  isRefreshingCampaign: false,
  isSubmittingTfv: false,
  isRefreshingTfv: false,
  isProvisioningNumber: false,
  numberActionError: null,

  async loadStatus(accountId) {
    set((s) => ({
      loadingStatusByAccount: { ...s.loadingStatusByAccount, [accountId]: true },
      errorByAccount: { ...s.errorByAccount, [accountId]: null },
    }));
    try {
      const api = getApiClient();
      // ApiClient auto-unwraps { success, data } → returns data directly.
      const data = await api.get<TelnyxStatusResponse>(
        API_ENDPOINTS.SUPER_ADMIN.ACCOUNTS.TELNYX.STATUS(accountId),
      );
      set((s) => ({
        statusByAccount: { ...s.statusByAccount, [accountId]: data },
        loadingStatusByAccount: { ...s.loadingStatusByAccount, [accountId]: false },
      }));
    } catch (err) {
      set((s) => ({
        loadingStatusByAccount: { ...s.loadingStatusByAccount, [accountId]: false },
        errorByAccount: { ...s.errorByAccount, [accountId]: toError(err) },
      }));
    }
  },

  async ensureProfile(accountId) {
    set({ isCreatingProfile: true });
    try {
      const api = getApiClient();
      await api.post(API_ENDPOINTS.SUPER_ADMIN.ACCOUNTS.TELNYX.CREATE_PROFILE(accountId), {});
      await get().loadStatus(accountId);
      set({ isCreatingProfile: false });
      return true;
    } catch (err) {
      set((s) => ({
        isCreatingProfile: false,
        errorByAccount: { ...s.errorByAccount, [accountId]: toError(err) },
      }));
      return false;
    }
  },

  async registerBrand(accountId) {
    set({ isCreatingBrand: true });
    try {
      const api = getApiClient();
      await api.post(API_ENDPOINTS.SUPER_ADMIN.ACCOUNTS.TELNYX.CREATE_BRAND(accountId), {});
      await get().loadStatus(accountId);
      set({ isCreatingBrand: false });
      return true;
    } catch (err) {
      set((s) => ({
        isCreatingBrand: false,
        errorByAccount: { ...s.errorByAccount, [accountId]: toError(err) },
      }));
      return false;
    }
  },

  async refreshBrand(accountId) {
    set({ isRefreshingBrand: true });
    try {
      const api = getApiClient();
      await api.get(API_ENDPOINTS.SUPER_ADMIN.ACCOUNTS.TELNYX.REFRESH_BRAND(accountId));
      await get().loadStatus(accountId);
      set({ isRefreshingBrand: false });
      return true;
    } catch (err) {
      set((s) => ({
        isRefreshingBrand: false,
        errorByAccount: { ...s.errorByAccount, [accountId]: toError(err) },
      }));
      return false;
    }
  },

  async createCampaign(accountId) {
    set({ isCreatingCampaign: true });
    try {
      const api = getApiClient();
      await api.post(API_ENDPOINTS.SUPER_ADMIN.ACCOUNTS.TELNYX.CREATE_CAMPAIGN(accountId), {});
      await get().loadStatus(accountId);
      set({ isCreatingCampaign: false });
      return true;
    } catch (err) {
      set((s) => ({
        isCreatingCampaign: false,
        errorByAccount: { ...s.errorByAccount, [accountId]: toError(err) },
      }));
      return false;
    }
  },

  async refreshCampaign(accountId) {
    set({ isRefreshingCampaign: true });
    try {
      const api = getApiClient();
      await api.get(API_ENDPOINTS.SUPER_ADMIN.ACCOUNTS.TELNYX.REFRESH_CAMPAIGN(accountId));
      await get().loadStatus(accountId);
      set({ isRefreshingCampaign: false });
      return true;
    } catch (err) {
      set((s) => ({
        isRefreshingCampaign: false,
        errorByAccount: { ...s.errorByAccount, [accountId]: toError(err) },
      }));
      return false;
    }
  },

  async submitTfv(accountId, params) {
    set({ isSubmittingTfv: true });
    try {
      const api = getApiClient();
      await api.post(
        API_ENDPOINTS.SUPER_ADMIN.ACCOUNTS.TELNYX.SUBMIT_TFV(accountId),
        params,
      );
      await get().loadStatus(accountId);
      set({ isSubmittingTfv: false });
      return true;
    } catch (err) {
      set((s) => ({
        isSubmittingTfv: false,
        errorByAccount: { ...s.errorByAccount, [accountId]: toError(err) },
      }));
      return false;
    }
  },

  async refreshTfv(accountId) {
    set({ isRefreshingTfv: true });
    try {
      const api = getApiClient();
      await api.get(API_ENDPOINTS.SUPER_ADMIN.ACCOUNTS.TELNYX.REFRESH_TFV(accountId));
      await get().loadStatus(accountId);
      set({ isRefreshingTfv: false });
      return true;
    } catch (err) {
      set((s) => ({
        isRefreshingTfv: false,
        errorByAccount: { ...s.errorByAccount, [accountId]: toError(err) },
      }));
      return false;
    }
  },

  async searchNumbers(accountId, criteria) {
    set({ isSearching: true, searchError: null });
    try {
      const api = getApiClient();
      const params = new URLSearchParams();
      if (criteria.country) params.set('country', criteria.country);
      if (criteria.areaCode) params.set('areaCode', criteria.areaCode);
      if (criteria.contains) params.set('contains', criteria.contains);
      if (criteria.type) params.set('type', criteria.type);
      if (criteria.capabilities?.length) params.set('capabilities', criteria.capabilities.join(','));
      if (criteria.limit) params.set('limit', String(criteria.limit));

      const url = `${API_ENDPOINTS.SUPER_ADMIN.ACCOUNTS.TELNYX.SEARCH_NUMBERS(accountId)}?${params.toString()}`;
      const data = await api.get<TelnyxAvailableNumber[]>(url);
      set({ searchResults: data, isSearching: false });
    } catch (err) {
      set({ isSearching: false, searchError: toError(err), searchResults: [] });
    }
  },

  clearSearch() {
    set({ searchResults: [], searchError: null });
  },

  async provisionNumber(accountId, params) {
    set({ isProvisioningNumber: true, numberActionError: null });
    try {
      const api = getApiClient();
      await api.post(
        API_ENDPOINTS.SUPER_ADMIN.ACCOUNTS.TELNYX.PROVISION_NUMBER(accountId),
        params,
      );
      await get().loadStatus(accountId);
      set({ isProvisioningNumber: false, searchResults: [] });
      return true;
    } catch (err) {
      set({ isProvisioningNumber: false, numberActionError: toError(err) });
      return false;
    }
  },

  async updateNumber(accountId, phoneNumberId, params) {
    set({ numberActionError: null });
    try {
      const api = getApiClient();
      await api.patch(
        API_ENDPOINTS.SUPER_ADMIN.ACCOUNTS.TELNYX.UPDATE_NUMBER(accountId, phoneNumberId),
        params,
      );
      await get().loadStatus(accountId);
      return true;
    } catch (err) {
      set({ numberActionError: toError(err) });
      return false;
    }
  },

  async releaseNumber(accountId, phoneNumberId) {
    set({ numberActionError: null });
    try {
      const api = getApiClient();
      await api.delete(
        API_ENDPOINTS.SUPER_ADMIN.ACCOUNTS.TELNYX.RELEASE_NUMBER(accountId, phoneNumberId),
      );
      await get().loadStatus(accountId);
      return true;
    } catch (err) {
      set({ numberActionError: toError(err) });
      return false;
    }
  },

  clearError(accountId) {
    if (accountId) {
      set((s) => ({ errorByAccount: { ...s.errorByAccount, [accountId]: null } }));
    } else {
      set({ errorByAccount: {}, searchError: null, numberActionError: null });
    }
  },
}));
